import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getBillingOverviewApi } from '../../api/adminBilling.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';

export default function DashboardBillingOverview({ loading: dashboardLoading }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchBillingOverview() {
      setLoading(true);
      const result = await apiHandler(() => getBillingOverviewApi());
      if (result.success) {
        setData(unwrapData(result));
      }
      setLoading(false);
    }
    fetchBillingOverview();
  }, []);

  const metrics = data?.metrics || {
    mrr: 0,
    activePaidSubscriptions: 0,
    pastDueCount: 0,
    trialsEndingThisWeek: 0,
  };

  const trendData = data?.revenueTrend || [];

  const isDataEmpty = trendData.length === 0;

  return (
    <div className="db-card h-full flex flex-col">
      <div className="db-card-header flex items-center justify-between border-b pb-3">
        <div className="db-card-title flex items-center gap-2 text-slate-800 font-bold">
          <i className="fas fa-hand-holding-dollar text-emerald-600" />
          Billing & MRR Overview
        </div>
        <div className="flex gap-3">
          <Link to="/payment-analytics" className="db-card-link text-xs font-semibold text-sky-800 hover:underline">
            Analytics
          </Link>
          <Link to="/refunds" className="db-card-link text-xs font-semibold text-sky-800 hover:underline">
            Refunds
          </Link>
        </div>
      </div>

      <div className="db-card-body flex-1 p-4 flex flex-col gap-4">
        {(dashboardLoading || loading) ? (
          <div className="db-skeleton-block h-48 w-full" />
        ) : (
          <>
            {/* Top Metrics Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Monthly Recurring Revenue</span>
                <span className="text-xl font-extrabold text-emerald-600">${metrics.mrr.toLocaleString()}</span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Active Paid Subs</span>
                <span className="text-xl font-extrabold text-slate-800">{metrics.activePaidSubscriptions}</span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Past Due Accounts</span>
                <span className={`text-xl font-extrabold ${metrics.pastDueCount > 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                  {metrics.pastDueCount}
                </span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Trials Ending (7d)</span>
                <span className="text-xl font-extrabold text-amber-600">{metrics.trialsEndingThisWeek}</span>
              </div>
            </div>

            {/* Revenue Trend Area Chart */}
            <div className="flex-1 min-h-[140px]">
              <span className="text-xs font-bold text-slate-700 mb-2 block">Revenue Growth (Last 6 Months)</span>
              {isDataEmpty ? (
                <div className="db-chart-empty text-slate-400 text-xs text-center py-8">No transaction data recorded yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={130}>
                  <AreaChart data={trendData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" stroke="#94A3B8" fontSize={9} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={9} tickLine={false} axisLine={false} />
                    <Tooltip 
                      contentStyle={{ background: '#0F172A', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11 }}
                      formatter={(value) => [`$${value}`, 'Revenue']}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
